import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const ApprovedPolicyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // 模拟已批准的保单数据
  const approvedPolicy = {
    id: id,
    policyNumber: 'AUTO-2024-003',
    type: 'Auto Insurance',
    client: 'Michael Brown',
    broker: 'John Smith',
    underwriter: 'Sarah Wilson',
    effectiveDate: '2024-01-30',
    approvedDate: '2024-01-24',
    documents: [
      {
        id: 1,
        name: 'Final Policy Document.pdf',
        uploadedAt: '2024-01-20',
        uploadedBy: 'John Smith',
        approvedAt: '2024-01-24',
        approvedBy: 'Sarah Wilson',
        type: 'Policy Document'
      },
      {
        id: 2,
        name: 'Approval Summary.pdf',
        uploadedAt: '2024-01-24',
        uploadedBy: 'Sarah Wilson',
        approvedAt: '2024-01-24',
        approvedBy: 'Sarah Wilson',
        type: 'Summary'
      }
    ],
    approvalHistory: [
      {
        date: '2024-01-20',
        author: 'John Smith',
        type: 'submission',
        content: 'Initial submission of all required documents'
      },
      {
        date: '2024-01-22',
        author: 'Sarah Wilson',
        type: 'review',
        content: 'All documents have been reviewed and verified'
      },
      {
        date: '2024-01-24',
        author: 'Sarah Wilson',
        type: 'approval',
        content: 'Policy has been approved and issued'
      }
    ]
  };

  const handleDocumentClick = (doc) => {
    const previewUrl = `/underwriter/document-preview/${doc.id}`;
    const documentState = { document: doc };
    const stateParam = encodeURIComponent(JSON.stringify(documentState));
    const fullUrl = `${previewUrl}?state=${stateParam}`;
    window.open(fullUrl, '_blank');
  };

  const handleDownload = (doc) => {
    console.log('Downloading document:', doc);
  };

  const handleExportAll = () => {
    console.log('Exporting all records...');
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/underwriter/policy-review')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Review Queue
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Approved Policy
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          View approved policy details and documentation
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Policy Information & Documents */}
        <Grid item xs={12} md={7}>
          {/* Policy Information Card */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
              Policy Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Policy Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {approvedPolicy.policyNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1">
                  {approvedPolicy.type}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1">
                  {approvedPolicy.client}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Broker
                </Typography>
                <Typography variant="body1">
                  {approvedPolicy.broker}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Effective Date
                </Typography>
                <Typography variant="body1">
                  {approvedPolicy.effectiveDate}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Approved Date
                </Typography>
                <Typography variant="body1">
                  {approvedPolicy.approvedDate}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Documents Section */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3 
            }}>
              <Typography variant="h6" sx={{ color: '#1976d2' }}>
                Approved Documents
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportAll}
                size="small"
              >
                Export All
              </Button>
            </Box>
            <List>
              {approvedPolicy.documents.map((doc) => (
                <ListItem
                  key={doc.id}
                  sx={{
                    bgcolor: '#f8f9fa',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <AttachFileIcon sx={{ mr: 2, color: '#1976d2' }} />
                  <ListItemText
                    primary={doc.name}
                    secondary={`Approved by ${doc.approvedBy} on ${doc.approvedAt}`}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Document">
                      <IconButton
                        size="small"
                        onClick={() => handleDocumentClick(doc)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(doc)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right Column - Approval History */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1976d2' }}>
              Approval History
            </Typography>
            <List>
              {approvedPolicy.approvalHistory.map((event, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                    pb: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ color: '#1976d2' }} />
                    <Typography variant="subtitle2">
                      {event.author}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.date}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ pl: 4 }}>
                    {event.content}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ApprovedPolicyDetailPage; 